/* eslint-disable jsx-a11y/iframe-has-title */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState, useEffect } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { ICONS } from "../constants/icons";
import { useUser } from "../contexts/UserContext";
import { ROUTES } from "../App";
import { get, off, onValue, ref } from "firebase/database";
import { database } from "../constants/firebase";
import useMoveTo from "../constants/navigation";
import MobileHeader from "../components/MobileHeader";
import { useLocation, useParams } from "react-router";
import ViewProfileFriends from "../components/ViewProfileFriends";
import ViewProfileTotalMembers from "../components/ViewProfileTotalMembers";
import BunnyVideoEmbed from "../components/BunnyVideoEmbed";
import { linkifyText } from "../utils/utils";
import Lottie from "lottie-react";
import fireAnimation from "../assets/animations/fire.json";
import JobCheckout from "../components/JobCheckout";
import { BsPersonCheck } from "react-icons/bs";

const ViewUserProfile = () => {
    const navigate = useMoveTo();
    const { userId } = useParams();
    const [user, setUser] = useState(null);
    const [totalMembers, setTotalMembers] = useState(0);
    const [loadingMembers, setLoadingMembers] = useState(true);
    const [streakDays, setStreakDays] = useState(0);
    const [videoThumbnail, setVideoThumbnail] = useState("");
    const [loadingUser, setLoadingUser] = useState(true);
    const [imageLoading, setImageLoading] = useState(true);
    const [isIframeLoading, setIsIframeLoading] = useState(true);
    const [friendCount, setFriendCount] = useState(0);
    const [loadingFriends, setLoadingFriends] = useState(true);
    const [viewFriends, setViewFriends] = useState(false);
    const [viewTotalMembers, setViewTotalMembers] = useState(false);
    const location = useLocation();
    const [checkoutData, setCheckoutData] = useState({ isOpen: false, applicant: null, amount: 0 });


    useEffect(() => {
        if (!userId) return;

        const userRef = ref(database, `Users/${userId}`); // 👈 reference to specific user node

        onValue(userRef, (snapshot) => {
            const userData = snapshot.val();
            if (userData) {
                setUser(userData);
            } else {
                setUser(null);
            }
            setLoadingUser(false);
        });

        // cleanup when component unmounts
        return () => off(userRef);
    }, [userId]);

    useEffect(() => {
        const getVideoThumbnail = (videoUrl) => {
            return new Promise((resolve) => {
                const video = document.createElement('video');
                video.src = videoUrl;
                video.crossOrigin = 'anonymous';

                video.addEventListener('loadedmetadata', () => {
                    video.currentTime = 0.5; // Capture at 0.5 seconds
                });

                video.addEventListener('seeked', () => {
                    const canvas = document.createElement('canvas');
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                    resolve(canvas.toDataURL('image/jpeg'));
                });

                video.addEventListener('error', () => {
                    resolve(IMAGES.statues); // Fallback image
                });
            });
        };

        if (user?.video) {
            getVideoThumbnail(user.video).then((thumbnail) => {
                setVideoThumbnail(thumbnail);
            });
        }
    }, [user?.video]);

    // Calculate total members across all hosted communities
    useEffect(() => {
        if (!user?.uid) return;

        const communitiesRef = ref(database, "Communities");

        const unsubscribe = onValue(communitiesRef, (snapshot) => {
            const data = snapshot.val();
            if (!data) {
                setTotalMembers(0);
                setStreakDays(0);

                setLoadingMembers(false);
                return;
            }

            const uniqueUsers = new Set();
            let maxStreak = 0; // 👈 track biggest streak

            for (const communityId in data) {
                const community = data[communityId];

                // 1️⃣ Only communities hosted by THIS user
                if (community.hostId !== user.uid) continue;

                // 2️⃣ Collect member IDs
                if (community.members) {
                    for (const memberId in community.members) {
                        uniqueUsers.add(memberId);
                    }
                }
                // 3️⃣ Get biggest streakDays
                const streak = Number(community.streakCount) || 0;
                if (streak > maxStreak) {
                    maxStreak = streak;
                }
            }

            // 3️⃣ (Optional) remove host if present
            uniqueUsers.delete(user.uid);

            // 4️⃣ FINAL COUNT
            setTotalMembers(uniqueUsers.size);
            setStreakDays(maxStreak);
            setLoadingMembers(false);
        });

        return () => off(communitiesRef);
    }, [user?.uid]);

    useEffect(() => {
        if (!user?.uid) return;

        const fetchFriendCount = async () => {
            try {
                const friendsSnap = await get(ref(database, `Users/${user.uid}/friends`));
                if (friendsSnap.exists()) {
                    const friendsData = friendsSnap.val();
                    setFriendCount(Object.keys(friendsData).length);
                } else {
                    setFriendCount(0);
                }
            } catch (err) {
                console.error("Error fetching friends:", err);
                setFriendCount(0);
            } finally {
                setLoadingFriends(false);
            }
        };

        fetchFriendCount();
    }, [user?.uid]);

    useEffect(() => {
        console.log("Is: ", location.state?.from);
    }, [location.state]);

    const handleBack = () => {
        if (location.state?.from === "explore") {
            navigate(ROUTES.DASHBOARD, { state: { activeTab: "Explore" } });
        } else if (location.state?.from === "creators") {
            navigate(ROUTES.DASHBOARD, { state: { activeTab: "Discover" } });
        } else if (location.state?.from === "job-applicants") {
            navigate(`${ROUTES.JOBS_APPLICANTS}/${location.state.jobId}`);
        } else {
            navigate(-1);
        }
    };

    if (loadingUser) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                {/* Spinner */}
                <div className="flex flex-col items-center space-y-3">
                    <div className="w-8 h-8 border-4 border-gray-300 border-t-[#327691] rounded-full animate-spin"></div>
                    <span className="text-gray-400 text-sm">Loading...</span>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="h-screen flex flex-col items-center justify-center bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                <div className="flex flex-col items-center space-y-3">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-12 h-12 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={1.5}
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                    </svg>
                    <p className="text-gray-400 text-base font-medium">
                        User not found
                    </p>
                    <button
                        onClick={() => handleBack()}
                        className="mt-4 px-4 py-2 text-sm bg-[#327691] text-white rounded-lg hover:bg-[#276072] transition"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen bg-white text-black dark:bg-[#1A1A1A] dark:text-white overflow-y-auto">
            {/* Mobile Header */}
            <MobileHeader title={"User Profile"} isBackBtn={true} handleBack={handleBack} />

            <div className="flex">
                {/* Sidebar */}
                <Sidebar />

                {/* Main Content */}
                <main className="flex-1 h-screen pt-5 pb-24 md:pb-20 px-6 overflow-y-auto bg-white text-black dark:bg-[#1A1A1A] dark:text-white">
                    {/* Profile Header */}
                    <div className="flex items-center justify-between px-2">
                        <button
                            onClick={() => handleBack()}
                            className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-xl md:text-3xl font-semibold hidden md:block">User Profile</h2>
                        <div className="w-5 block" />
                    </div>

                    {/* Profile Card */}
                    <div className="mt-6 flex flex-wrap items-center gap-4">
                        {/* Profile Image */}
                        <div className="flex-shrink-0 flex flex-col items-center">

                            <div className="relative w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden">
                                {imageLoading && (
                                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700 rounded-full" />
                                )}
                                <img
                                    src={
                                        user?.profilePic ||
                                        'https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media&token=2d65470d-2104-4787-a5ef-cbfceb8a63ef'
                                    }
                                    alt="Profile"
                                    className={`w-full h-full object-cover rounded-full transition-opacity duration-300 ${imageLoading ? 'opacity-0' : 'opacity-100'
                                        }`}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => setImageLoading(false)}
                                />
                            </div>

                            {user?.role === "Host" && streakDays > 0 && (
                                <div className="mt-1 flex items-center gap-1 bg-white rounded-full px-1.5 py-0.5 shadow">
                                    <Lottie
                                        animationData={fireAnimation}
                                        loop
                                        autoplay
                                        className="w-4 h-4"
                                    />
                                    <span className="text-[10px] font-bold text-black">
                                        {streakDays}
                                    </span>
                                </div>
                            )}

                        </div>

                        {/* User Info */}
                        <div className="flex-1 min-w-0 space-y-1 overflow-hidden">
                            <h3
                                className={`flex items-center gap-2 text-xl md:text-2xl font-bold ${user?.role === "Host" ? "dark:glow-text-dark glow-text-light" : ""}`}
                            >
                                <span className="block max-w-[90%] truncate">
                                    {user?.name || 'Unknown User'}
                                </span>

                                {user?.isTeamUser && (
                                    <svg width="24px" height="24px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#327691">
                                        <path d="M10.5213 2.62368C11.3147 1.75255 12.6853 1.75255 13.4787 2.62368L14.4989 3.74391C14.8998 4.18418 15.4761 4.42288 16.071 4.39508L17.5845 4.32435C18.7614 4.26934 19.7307 5.23857 19.6757 6.41554L19.6049 7.92905C19.5771 8.52388 19.8158 9.10016 20.2561 9.50111L21.3763 10.5213C22.2475 11.3147 22.2475 12.6853 21.3763 13.4787L20.2561 14.4989C19.8158 14.8998 19.5771 15.4761 19.6049 16.071L19.6757 17.5845C19.7307 18.7614 18.7614 19.7307 17.5845 19.6757L16.071 19.6049C15.4761 19.5771 14.8998 19.8158 14.4989 20.2561L13.4787 21.3763C12.6853 22.2475 11.3147 22.2475 10.5213 21.3763L9.50111 20.2561C9.10016 19.8158 8.52388 19.5771 7.92905 19.6049L6.41553 19.6757C5.23857 19.7307 4.26934 18.7614 4.32435 17.5845L4.39508 16.071C4.42288 15.4761 4.18418 14.8998 3.74391 14.4989L2.62368 13.4787C1.75255 12.6853 1.75255 11.3147 2.62368 10.5213L3.74391 9.50111C4.18418 9.10016 4.42288 8.52388 4.39508 7.92905L4.32435 6.41553C4.26934 5.23857 5.23857 4.26934 6.41554 4.32435L7.92905 4.39508C8.52388 4.42288 9.10016 4.18418 9.50111 3.74391L10.5213 2.62368Z" stroke="#327691" strokeWidth="1.5"></path>
                                        <path d="M9 12L11 14L15 10" stroke="#327691" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path>
                                    </svg>
                                )}
                            </h3>

                            <p className="text-sm md:text-base text-primary truncate">{user.niche}</p>
                            <div className="flex items-center space-x-4 text-sm md:text-base">
                                {user?.role === "Host" && (
                                    <button onClick={() => setViewTotalMembers(true)}>
                                        <div className="flex items-center space-x-2">
                                            <strong>{loadingMembers ? '...' : totalMembers}</strong>
                                            <span className="dark:text-white text-black">Total members</span>
                                        </div>
                                    </button>
                                )}

                                <button
                                    className=""
                                    onClick={() => setViewFriends(true)}
                                >
                                    <div className="flex items-center space-x-2">
                                        <strong>{loadingFriends ? '...' : friendCount}</strong>
                                        <span className="dark:text-white text-black">Friends</span>
                                    </div>
                                </button>
                            </div>

                            {/* State-aware Job Hiring Action */}
                            {location.state?.from === "job-applicants" && (
                                <div className="mt-3 flex items-center gap-2">
                                    <button
                                        onClick={() => {
                                            const budget = location.state.job?.budget || "";
                                            const matches = budget.match(/\d+/g);
                                            const amount = matches ? parseFloat(matches[0].replace(/,/g, '')) : 0;
                                            
                                            setCheckoutData({
                                                isOpen: true,
                                                applicant: { ...user, applicantName: user.name },
                                                amount: amount
                                            });
                                        }}
                                        className="px-4 py-2 bg-[#327691] text-white rounded-xl text-sm font-semibold hover:bg-[#276072] transition shadow-lg shadow-[#327691]/20 flex items-center gap-2"
                                    >
                                        <BsPersonCheck className="w-4 h-4" />
                                        Hire instead
                                    </button>
                                    <p className="text-xs text-gray-400 italic">
                                        For: {location.state.job?.title}
                                    </p>
                                </div>
                            )}

                            {user?.website && (
                                <a
                                    href="#"
                                    onClick={(e) => {
                                        e.preventDefault();

                                        const website = user.website.startsWith('http')
                                            ? user.website
                                            : `https://${user.website}`;

                                        // 👇 Send message to the React Native WebView
                                        if (window.ReactNativeWebView) {
                                            window.ReactNativeWebView.postMessage(
                                                JSON.stringify({
                                                    type: 'OPEN_EXTERNAL_WEBSITE',
                                                    url: website,
                                                })
                                            );
                                        } else {
                                            // Fallback for normal browsers
                                            window.open(website, '_blank', 'noopener,noreferrer');
                                        }
                                    }}
                                    className="block text-sm md:text-base text-primary truncate"
                                >
                                    {user.website}
                                </a>
                            )}

                            {/* Bio */}
                            {user?.bio && (
                                <p className="text-sm md:text-base text-black dark:text-[#CCCCCC] truncate">
                                    {linkifyText(user.bio, false, "text-primary")}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Portfolio Section */}
                    <div className="mt-8 border-t border-[#D9D9D9]/20 pt-4">
                        {/* If video exists, show Showreel */}
                        {user?.video ? (
                            <BunnyVideoEmbed
                                videoUrl={user.video}
                                orientation={user.videoOrientation}
                                videoWidth={user.videoWidth}
                                videoHeight={user.videoHeight}
                                variant="inline"
                                isLoading={isIframeLoading}
                                onLoad={() => setIsIframeLoading(false)}
                                autoplay={true}
                                muted={true}
                                wrapperClassName="mx-auto shadow-xl"
                                iframeClassName={isIframeLoading ? "opacity-0 transition-opacity duration-300" : "opacity-100 transition-opacity duration-300"}
                                title={`${user.name || "Profile"} showreel`}
                            />
                        ) : user?.portfolioBanner ? (
                            // 🖼️ Portfolio image with shimmer
                            <div className="relative w-full h-64 md:h-80 rounded-xl overflow-hidden bg-gray-200 dark:bg-gray-800">
                                {imageLoading && (
                                    <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-300 via-gray-200 to-gray-300 dark:from-gray-700 dark:via-gray-600 dark:to-gray-700" />
                                )}
                                <img
                                    src={user.portfolioBanner}
                                    alt="Portfolio Banner"
                                    className={`w-full h-full object-cover rounded-xl transition-opacity duration-300 ${imageLoading ? "opacity-0" : "opacity-100"}`}
                                    onLoad={() => setImageLoading(false)}
                                    onError={() => setImageLoading(false)}
                                />
                            </div>
                        ) : (
                            // Fallback if neither exists
                            <p className="text-center text-gray-500">
                                No portfolio added yet
                            </p>
                        )}
                    </div>


                    <div className="mt-10 flex flex-col md:flex-row gap-8">

                        {/* About Section */}
                        <div className="md:w-1/2">
                            <h4 className="text-lg md:text-xl font-semibold mb-4 text-primary">About</h4>
                            {user && (
                                <p className="text-black dark:text-[#CCCCCC] text-sm md:text-base whitespace-pre-line">
                                    {user.about || 'No about information provided yet.'}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* View Friends Modal */}
                    {viewFriends && (
                        <ViewProfileFriends
                            isOpen={viewFriends}
                            onClose={() => setViewFriends(false)}
                            userId={user.uid}
                        />
                    )}

                    {/* View Total Members Modal */}
                    {viewTotalMembers && (
                        <ViewProfileTotalMembers
                            isOpen={viewTotalMembers}
                            onClose={() => setViewTotalMembers(false)}
                            userId={user.uid}
                        />
                    )}

                    {/* Job Checkout Modal */}
                    {checkoutData.isOpen && (
                        <JobCheckout
                            isOpen={checkoutData.isOpen}
                            onClose={() => setCheckoutData({ ...checkoutData, isOpen: false })}
                            job={location.state?.job}
                            applicant={checkoutData.applicant}
                            basePrice={checkoutData.amount}
                        />
                    )}

                </main>
            </div>
        </div>
    );
};

export default ViewUserProfile;
